"use client"

import { useRef } from "react"
import { X, Printer, FileText } from "lucide-react"
import type { LearningReport } from "@/hooks/useReportGenerator"

interface ReportModalProps {
  report: LearningReport
  onClose: () => void
}

export function ReportModal({ report, onClose }: ReportModalProps) {
  const printRef = useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    const content = printRef.current?.innerHTML
    if (!content) return
    const win = window.open("", "_blank")
    if (!win) return
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${report.title} — ${report.studentName}</title>
        <meta charset="utf-8" />
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Georgia, 'Times New Roman', serif; color: #1e293b; background: white; padding: 48px; max-width: 720px; margin: 0 auto; }
          .report-header { border-bottom: 2px solid #1e293b; padding-bottom: 20px; margin-bottom: 32px; }
          .report-title { font-size: 26px; font-weight: 700; letter-spacing: -0.5px; }
          .report-subtitle { font-size: 14px; color: #64748b; margin-top: 4px; }
          .meta-row { display: flex; gap: 24px; margin-top: 12px; font-size: 12px; color: #475569; }
          .meta-row span { font-weight: 600; }
          .section { margin-bottom: 28px; }
          .section-heading { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #475569; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 12px; }
          p { font-size: 14px; line-height: 1.75; color: #334155; margin-bottom: 10px; }
          .report-footer { margin-top: 48px; border-top: 1px solid #e2e8f0; padding-top: 16px; font-size: 11px; color: #94a3b8; }
        </style>
      </head>
      <body>${content}</body>
      </html>
    `)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 400)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">{report.title}</p>
              <p className="text-xs text-slate-500">{report.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-2 rounded-lg transition-all active:scale-95"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Report body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* This div is used for the print source */}
          <div ref={printRef}>
            {/* Print-friendly header */}
            <div className="report-header">
              <div className="text-xl font-bold text-slate-900 tracking-tight">{report.title}</div>
              <div className="text-sm text-slate-500 mt-0.5">{report.subtitle}</div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500 font-medium">
                <span>Student: <strong className="text-slate-700">{report.studentName}</strong></span>
                <span>Level: <strong className="text-slate-700">{report.classLevel}</strong></span>
                <span>System: <strong className="text-slate-700">{report.examBody}</strong></span>
                <span>Generated: <strong className="text-slate-700">{report.generatedAt}</strong></span>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-6 mt-5">
              {report.sections.map((section, i) => (
                <div key={i} className="section">
                  <h3 className="section-heading text-[11px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-2 mb-3">
                    {section.heading}
                  </h3>
                  <div className="space-y-2.5">
                    {section.paragraphs.map((para, j) => (
                      <p key={j} className="text-sm text-slate-700 leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="report-footer mt-8 pt-4 border-t border-slate-100 text-[11px] text-slate-400">
              <p>Generated by EarlyScape on {report.generatedAt}. This report is based on platform activity and should be considered alongside teacher assessments and academic records.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
